import { useState } from "react";
import { useProductSections, ProductSectionFormData, ProductSection } from "@/features/produtos/hooks/useProductSections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Ban } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function ProductSections() {
  const { sections, isLoading, createSection, updateSection, deleteSection } = useProductSections();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<ProductSection | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ProductSectionFormData>({
    descricao: "",
    referencia: "",
    isInativo: false,
  });
  const [busca, setBusca] = useState("");

  const sectionsFiltradas = busca
    ? sections.filter(s =>
        s.descricao?.toLowerCase().includes(busca.toLowerCase()) ||
        (s.referencia ?? "").toLowerCase().includes(busca.toLowerCase()))
    : sections;

  const paginacao = usePaginacaoLocal(sectionsFiltradas);

  const handleOpenDialog = (section?: ProductSection) => {
    if (section) {
      setEditingSection(section);
      setFormData({
        descricao: section.descricao || "",
        referencia: section.referencia || "",
        isInativo: section.isInativo,
      });
    } else {
      setEditingSection(null);
      setFormData({ descricao: "", referencia: "", isInativo: false });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingSection) {
      await updateSection.mutateAsync({ id: editingSection.id, ...formData });
    } else {
      await createSection.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Seções de Produtos</h1>
          <p className="text-muted-foreground">Gerencie as seções de produtos</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Seção
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição ou referência..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((section) => (
              <TableRow key={section.id}>
                <TableCell className="font-medium">{section.descricao || "-"}</TableCell>
                <TableCell>{section.referencia || "-"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={section.isInativo ? "secondary" : "success"}>
                    {section.isInativo ? "Inativo" : "Ativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(section)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!section.isInativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(section.id)}>
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhuma seção encontrada para esta busca." : "Nenhuma seção cadastrada"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSection ? "Editar Seção" : "Nova Seção"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="referencia">Referência</Label>
              <Input
                id="referencia"
                value={formData.referencia}
                onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="ativo"
                checked={!formData.isInativo}
                onCheckedChange={(v) => setFormData({ ...formData, isInativo: !v })}
              />
              <Label htmlFor="ativo">Ativo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar seção</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar esta seção de produto?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { deleteSection.mutate(inativarId); setInativarId(null); } }}>
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
